import { MapViewState, PaneState } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMap';
import { computed, onMounted, onUnmounted, reactive, Ref, ref, UnwrapRef } from '@vue/composition-api';
import {
  PANE_SIDE_HARD_MAX_WIDTH_PX,
  PANE_SIDE_MAX_WIDTH_RATE,
  PANE_SIDE_MIN_WIDTH_RATE,
  VERTICAL_PANE_PADDING_NUMBER,
} from '@/components/ExternalReports/HokkaidoAirport/consts';
import ExtremeMap from '@/components/lib/ExtremeMap/index.vue';
import { useStore } from '@/hooks/useStore';
import { Settings as UserSettings } from '@/models/apis/user/userResponse';
import type { HokkaidoAirportCarExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportCar';
import type { HokkaidoAirportMtxExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMtx';
import type { HokkaidoAirportReportExt, HokkaidoAirportReportPhotoExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import type {
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import {
  isHokkaidoAirportExternalReportSettings,
} from '@/lib/externalReports/externalReportHelper';
import { dtFormat } from '@/lib/dateHelper';
import { NO_PHOTO_IMAGE, EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/hokkaidoAirport';

interface RedrawCarLayerOptions {
  fitToExtent?: boolean;
  setSelectedCarToCenter?: boolean;
}
export interface UseExtremeMapResult {
  extremeMapRef: Ref;
  mapViewState: UnwrapRef<MapViewState>;
  loadExtremeMapEssentials: () => Promise<void>;
  forceResizeExtremeMap: () => void;
  redrawCarLayer: (options: RedrawCarLayerOptions) => void;
  showHokkaidoAirportReportLayer: (
    selectedReport: HokkaidoAirportReportExt,
    fitToExtent: boolean,
    showTrack: boolean,
  ) => void;
  removeHokkaidoAirportReportLayer: () => void;
  showHokkaidoAirportCarPopup: (car: HokkaidoAirportCarExt) => void;
  showHokkaidoAirportReportPhotoPopup: (photo: HokkaidoAirportReportPhotoExt) => void;
  showHokkaidoAirportMtxPopup: (mtx: HokkaidoAirportMtxExt) => void;
  hidePopup: () => void;
}

export function useExtremeMap(paneState: PaneState): UnwrapRef<UseExtremeMapResult> {
  const store = useStore();
  const userState = store.state.user;
  const userSettings = computed<UserSettings>(() => {
    return userState.settings;
  });
  const hokkaidoAirportUserSettings = computed<HokkaidoAirportSettings | null>(() => {
    const hokkaidoAirportUserSettings = window.master.externalReportSettings?.find(
      (setting) => setting.external_report_type === EXTERNAL_REPORT_TYPE,
    );
    if (!hokkaidoAirportUserSettings) return null;
    if (!isHokkaidoAirportExternalReportSettings(hokkaidoAirportUserSettings)) return null;

    return hokkaidoAirportUserSettings;
  });

  const extremeMapRef = ref<InstanceType<typeof ExtremeMap>>();

  const mapViewState = reactive<MapViewState>({
    extremeMapEssentials: null,
    onResizeFunc: () => {},
  });

  const loadExtremeMapEssentials = async(): Promise<void> => {
    await waitForUserAndMasters();
    mapViewState.extremeMapEssentials = {
      userSettings: userSettings.value,
      kpMap: window.master.kpMap,
      externalReportSettings: hokkaidoAirportUserSettings.value,
    };
  };

  const resizeMap = (mapHeight: number) => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.setMapHeight(mapHeight);
    extremeMapRef.value.triggerResize();
  };

  const initResizeFunc = () => {
    mapViewState.onResizeFunc = () => {
      const headerH = 57;
      const searchBarH = 96;
      const h = window.innerHeight - headerH - searchBarH;
      const w = window.innerWidth - VERTICAL_PANE_PADDING_NUMBER;
      const paneStyle = paneState.paneStyle;
      paneStyle.paneSideMinWidth = Math.min(Math.floor(w * PANE_SIDE_MIN_WIDTH_RATE), PANE_SIDE_HARD_MAX_WIDTH_PX);
      paneStyle.paneSideMaxWidth = Math.min(Math.floor(w * PANE_SIDE_MAX_WIDTH_RATE), PANE_SIDE_HARD_MAX_WIDTH_PX);
      paneStyle.listMinHeight = Math.floor(h * (paneState.paneStyleLimits?.listMinHeightRate ?? 1.0));
      paneStyle.listMaxHeight = Math.floor(h * (paneState.paneStyleLimits?.listMaxHeightRate ?? 1.0));
      resizeMap(h);
    };
    mapViewState.onResizeFunc();
    window.addEventListener('resize', mapViewState.onResizeFunc);
  };

  const forceResizeExtremeMap = (): void => {
    mapViewState.onResizeFunc();
  };

  const redrawCarLayer = ({ fitToExtent, setSelectedCarToCenter }: RedrawCarLayerOptions = {}) => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.redrawExternalCarLayer({
      hideCarIcons: !paneState.showCarIcons,
      photoTypeVisibility: paneState.photoTypeVisibility,
      fitToExtent: !!fitToExtent,
      setSelectedCarToCenter,
      externalReportUserSettings: hokkaidoAirportUserSettings.value,
      showDirectionArrow: true,
    });
  };

  const getHokkaidoAirportCarPopupContentInnerHtml = (car: HokkaidoAirportCarExt): string => {
    let html = `<p class="title">【${car.hanName}】</p>`;
    const pcLabels = hokkaidoAirportUserSettings.value?.pc;
    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.field_label?.status ?? '状態'}: ${car.statusDisp}</span>
      </p>`;
    if (!car.isMoving || !car.reportExt) {
      return html;
    }

    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.field_label?.airport_name ?? '空港名'}: ${car.reportExt.airportName}</span>
      </p>`;
    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.table_header?.standby_type ?? '待機形態'}: ${car.reportExt.standbyType ?? ''}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.departure_ts ?? '業務開始時刻'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.baseDepartureTs, 'HH:MM')}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.start_work_ts ?? '作業開始時刻'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.operationStartTs, 'HH:MM')}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.end_work_ts ?? '作業終了時刻'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.operationEndTs, 'HH:MM')}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.table_header?.arrival_ts ?? '業務終了時刻'}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.baseArrivalTs, 'HH:MM')}</span>
      </p>`;
    return html;
  };

  const getHokkaidoAirportReportInfoContentInnerHtml = (info: HokkaidoAirportReportPhotoExt | HokkaidoAirportMtxExt): string => {
    const pcLabels = hokkaidoAirportUserSettings.value?.pc;
    let html = `
      <p class="info-row">
        <span class="val">${pcLabels?.field_label?.han_name ?? 'ユーザー名'}:</span>
        <span class="val ml2">${info.hanName}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels?.field_label?.work_time ?? '作業日時'}:</span>
        <span class="val ml2">${dtFormat(info.ts)}</span>
      </p>`;
    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.field_label?.airport_name ?? '空港名'}: ${info.airportName}</span>
      </p>`;
    html += `
      <p class="info-row-wrap">
        <span>${pcLabels?.table_header?.standby_type ?? '待機形態'}: ${info.standbyType ?? ''}</span>
      </p>`;
    return html;
  };

  const getHokkaidoAirportReportPhotoPopupContentInnerHtml = (photo: HokkaidoAirportReportPhotoExt): string => {
    const photoTypeDisp = photo.photoType ?? '';
    let html =
      `<p class="title">【${photoTypeDisp}写真${photo.dispOrder}】</p>`;
    html += getHokkaidoAirportReportInfoContentInnerHtml(photo);
    html += `
      <div class="image-area" id="external-map-photo${photo.id}">
        <div class="image-box">
          <div class="image-wrap">
            <img src="${photo.savedImage ?? NO_PHOTO_IMAGE}" class="image" />
          </div>
        </div>
      </div>`;
    return html;
  };

  const showHokkaidoAirportReportLayer = (
    selectedReport: HokkaidoAirportReportExt,
    fitToExtent: boolean,
    showTrack: boolean,
  ): void => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.showExternalReportLayer(selectedReport, {
      photoTypeVisibility: paneState.photoTypeVisibility,
      fitToExtent: fitToExtent,
      externalReportUserSettings: hokkaidoAirportUserSettings.value,
      showTrack,
    });
  };

  const removeHokkaidoAirportReportLayer = (): void => {
    if (!extremeMapRef.value) return;
    extremeMapRef.value.removeExternalReportLayer();
  };

  const showHokkaidoAirportCarPopup = (car: HokkaidoAirportCarExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getHokkaidoAirportCarPopupContentInnerHtml(car);
    extremeMapRef.value.showExternalCarPopup(car, innerHTML);
  };

  const showHokkaidoAirportReportPhotoPopup = (photo: HokkaidoAirportReportPhotoExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getHokkaidoAirportReportPhotoPopupContentInnerHtml(photo);
    extremeMapRef.value.showExternalReportPhotoPopup(photo, innerHTML);
  };

  const showHokkaidoAirportMtxPopup = (mtx: HokkaidoAirportMtxExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getHokkaidoAirportReportInfoContentInnerHtml(mtx);
    extremeMapRef.value.showExternalMtxPopup(mtx, innerHTML);
  };

  const hidePopup = (): void => {
    if (!extremeMapRef.value) return;
    extremeMapRef.value.hidePopup();
  };

  onMounted(async() => {
    initResizeFunc();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', mapViewState.onResizeFunc);
  });

  return {
    extremeMapRef,
    mapViewState,
    loadExtremeMapEssentials,
    forceResizeExtremeMap,
    redrawCarLayer,
    showHokkaidoAirportReportLayer,
    removeHokkaidoAirportReportLayer,
    showHokkaidoAirportCarPopup,
    showHokkaidoAirportReportPhotoPopup,
    showHokkaidoAirportMtxPopup,
    hidePopup,
  };
}
